import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { baseRequest } from '../../_common/core/http_services'
import { showAxiosError } from '../utils/messenger'
import { deleteCookieByName, deleteHostCookieByName, setHostCookie } from '../utils/cookies'

const MINIMIZED_STORAGE_KEY = 'ttf.dashboardActionBanner.minimizedForKey'
const DID_ANIMATE_STORAGE_KEY = 'ttf.dashboardActionBanner.didAnimate'
const DASHBOARD_FEEDBACK_SOURCE = 'Dashboard 2.0'

const getDashboardAction = user => {
  const step = Number.isFinite(Number(user?.newDashboardStep)) ? Number(user?.newDashboardStep) : null

  const canJoinWaitlist =
    step !== null
      ? step === 1
      : !!user?.showNewDashboardWaitlist &&
        !user?.onNewDashboardWaitlist &&
        !user?.showNewDashboardInvite &&
        !user?.hasNewDashboardAccess

  // Treat rollback (step 5) as "invite ready" too
  const canAcceptInvitation =
    step !== null ? step === 3 || step === 5 : !!user?.showNewDashboardInvite && !user?.hasNewDashboardAccess

  if (canJoinWaitlist) {
    return {
      key: 'join_waitlist',
      eyebrow: 'The next generation of Ticket Fairy is coming',
      title: 'Join the waitlist to get early access',
      cta: 'Join waitlist',
      apiAction: 'join_waitlist',
    }
  }

  if (canAcceptInvitation) {
    return {
      key: 'accept_invitation',
      eyebrow: 'The next generation of Ticket Fairy is here!',
      title: 'Accept your invite to access the new dashboard',
      cta: 'Accept invite',
      apiAction: 'accept_invitation',
    }
  }

  if (user?.hasNewDashboardAccess) {
    return {
      key: 'new_dashboard_ready',
      eyebrow: 'Access to the next generation of Ticket Fairy is here!',
      title: 'Access your new dashboard now',
      cta: 'Access dashboard',
      apiAction: 'new_dashboard_ready',
    }
  }

  return null
}

const isUserOnWaitlist = user => {
  const step = Number.isFinite(Number(user?.newDashboardStep)) ? Number(user?.newDashboardStep) : null
  if (step !== null) return step === 2
  return !!user?.onNewDashboardWaitlist
}

const getMinimizeKey = (action, user) => {
  if (action?.key) return action.key
  if (isUserOnWaitlist(user)) return 'waitlist'
  return null
}

const styles = {
  wrapper: {
    position: 'fixed',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: 24,
    zIndex: 1000000,
    maxWidth: 'calc(100% - 48px)',
    minWidth: 320,
    pointerEvents: 'none',
  },
  card: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
    padding: '16px 24px',
    background: 'rgba(11, 10, 21, 0.6)',
    backdropFilter: 'blur(30px)',
    WebkitBackdropFilter: 'blur(30px)',
    border: '1px solid rgba(235, 235, 238, 0.15)',
    borderRadius: 16,
    boxShadow: '0 24px 70px rgba(0, 0, 0, 0.45)',
    pointerEvents: 'auto',
  },
  miniWrapper: {
    position: 'fixed',
    left: 10,
    bottom: 10,
    zIndex: 2000,
    maxWidth: 'calc(100% - 48px)',
    pointerEvents: 'none',
    // Match the sidebar open/close animation (.sidebar-close() => transition(all, .3))
    transition: 'left 0.3s ease',
  },
  miniIconBtn: {
    width: 44,
    height: 44,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(11, 10, 21, 0.6)',
    backdropFilter: 'blur(30px)',
    WebkitBackdropFilter: 'blur(30px)',
    border: '1px solid rgba(235, 235, 238, 0.15)',
    boxShadow: '0 24px 70px rgba(0, 0, 0, 0.45)',
    pointerEvents: 'auto',
    cursor: 'pointer',
    padding: 0,
  },
  mobile: `
    @media (max-width: 768px) {
      .ttf-dashboard-action-banner {
        bottom: 16px !important;
        left: 16px !important;
        right: 16px !important;
        transform: none !important;
        max-width: none !important;
        min-width: 0 !important;
      }
      .ttf-dashboard-action-banner--minimized {
        bottom: 16px !important;
        left: 16px !important;
        right: auto !important;
        transform: none !important;
        max-width: calc(100% - 32px) !important;
      }
      .ttf-dashboard-action-banner__card {
        flex-wrap: wrap;
        gap: 12px;
        padding: 12px 16px;
      }
      .ttf-dashboard-action-banner__text {
        flex: 1 1 100%;
      }
      .ttf-dashboard-action-banner__eyebrow,
      .ttf-dashboard-action-banner__title {
        white-space: normal;
      }
    }
    @media (max-width: 600px) {
      .ttf-dashboard-action-banner__card {
        flex-direction: column !important;
        align-items: center !important;
        gap: 16px !important;
      }
      .ttf-dashboard-action-banner__text {
        text-align: center !important;
        width: 100%;
      }
      .ttf-dashboard-action-banner__eyebrow,
      .ttf-dashboard-action-banner__title {
        text-align: center !important;
        white-space: normal !important;
      }
    }
    @media (max-width: 480px) {
      .ttf-dashboard-action-banner__eyebrow {
        font-size: 12px;
        line-height: 18px;
      }
      .ttf-dashboard-action-banner__title {
        font-size: 14px;
        line-height: 21px;
      }
    }
  `,
  buttonCss: `
    .ttf-dashboard-action-banner__btn {
      border-radius: 999px;
      padding: 0 24px;
      backdrop-filter: blur(0.75px);
      -webkit-backdrop-filter: blur(0.75px);
      border: none;
      outline: none;
      appearance: none;
      -webkit-appearance: none;
      background: transparent;
      color: transparent;
      background-image: linear-gradient(138.91deg, #ffbfb5 22.107%, #c4b5ff 55.634%, #b5bfff 84.734%);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      font-family: "Inter Tight", "Inter_Tight:Medium", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      font-weight: 500;
      white-space: nowrap;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      text-decoration: none;
      cursor: pointer;
      transition: all 100ms linear;
      position: relative;
      box-sizing: border-box;
      min-height: 42px;
      height: 42px;
      z-index: 1;
      font-size: 14px;
      line-height: 21px;
      letter-spacing: 0.1px;
    }

    /* Gradient border using pseudo-element with mask */
    .ttf-dashboard-action-banner__btn::before {
      content: '';
      position: absolute;
      inset: -1px;
      border-radius: 999px;
      background: linear-gradient(138.91deg, #ffbfb5 22.107%, #c4b5ff 55.634%, #b5bfff 84.734%);
      z-index: -1;
      -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: destination-out;
      mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      mask-composite: exclude;
      padding: 1px;
    }

    .ttf-dashboard-action-banner__btn:hover {
      opacity: 0.9;
    }

    .ttf-dashboard-action-banner__btn:active {
      opacity: 0.8;
      transform: translateY(0);
    }

    .ttf-dashboard-action-banner__btn:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    .ttf-dashboard-action-banner__btn::-moz-focus-inner {
      border: 0;
      padding: 0;
    }

    @keyframes ttfDashboardSpinner {
      to { transform: rotate(360deg); }
    }

    .ttf-dashboard-action-banner__spinner {
      width: 16px;
      height: 16px;
      border-radius: 999px;
      border: 2px solid rgba(235, 235, 238, 0.25);
      border-top-color: rgba(235, 235, 238, 0.9);
      animation: ttfDashboardSpinner 700ms linear infinite;
      display: inline-block;
    }

    .ttf-dashboard-action-banner__sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .ttf-dashboard-action-banner__close {
      position: absolute;
      top: -10px;
      right: -10px;
      width: 28px;
      height: 28px;
      border-radius: 999px;
      border: 1px solid rgba(235, 235, 238, 0.15);
      background: rgb(17 15 25);
      backdrop-filter: blur(30px);
      -webkit-backdrop-filter: blur(30px);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
      color: #ebebee;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 0;
      line-height: 1;
      font-size: 16px;
      pointer-events: auto;
      transition: opacity 100ms linear, transform 100ms linear;
    }

    .ttf-dashboard-action-banner__close:hover {
      opacity: 0.95;
    }

    .ttf-dashboard-action-banner__close:active {
      opacity: 0.8;
      transform: scale(0.98);
    }

    @keyframes ttfDashboardActionBannerFadeInUp {
      from {
        opacity: 0;
        transform: translateY(12px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes ttfDashboardActionBannerFadeOutDown {
      from {
        opacity: 1;
        transform: translateY(0);
      }
      to {
        opacity: 0;
        transform: translateY(12px);
      }
    }

    .ttf-dashboard-action-banner__card--preanimate {
      opacity: 0;
      transform: translateY(12px);
      pointer-events: none;
    }

    .ttf-dashboard-action-banner__card--animate {
      animation: ttfDashboardActionBannerFadeInUp 220ms ease-out both;
    }

    .ttf-dashboard-action-banner__card--exit {
      animation: ttfDashboardActionBannerFadeOutDown 220ms ease-in both;
      pointer-events: none;
    }

    .ttf-dashboard-action-banner__mini-present {
      width: 20px;
      height: 20px;
      display: block;
      color: rgba(235, 235, 238, 0.95);
    }
  `,
  badge: {
    background: 'rgba(235, 235, 238, 0.15)',
    borderRadius: 8,
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '21px',
    color: '#ebebee',
    textAlign: 'center',
    letterSpacing: '0.1px',
  },
  text: { flex: 1, minWidth: 0 },
  eyebrow: {
    color: '#9f9cb0',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '21px',
    marginBottom: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    letterSpacing: '0.1px',
  },
  title: {
    color: '#ebebee',
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 500,
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    letterSpacing: '0.1px',
  },
}

const DashboardActionBanner = ({ user, onUpdated }) => {
  const action = useMemo(() => getDashboardAction(user), [user])
  const derivedOnWaitlist = useMemo(() => isUserOnWaitlist(user), [user])
  const isRolledBack = useMemo(() => {
    const step = Number.isFinite(Number(user?.newDashboardStep)) ? Number(user?.newDashboardStep) : null
    return step === 5
  }, [user])
  const [loading, setLoading] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [animateIn, setAnimateIn] = useState(false)
  const [suppressActionBanner, setSuppressActionBanner] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [isSuccessExiting, setIsSuccessExiting] = useState(false)
  const [forcedOnWaitlist, setForcedOnWaitlist] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      return document?.body?.classList?.contains('sidebar-open') || false
    } catch (_) {
      return false
    }
  })
  const [isMobile, setIsMobile] = useState(() => {
    try {
      return window?.innerWidth <= 768
    } catch (_) {
      return false
    }
  })
  const [hiddenUntilAnimate, setHiddenUntilAnimate] = useState(() => {
    // On the very first page load in this tab, keep the banner hidden until the delayed animation runs.
    try {
      const didAnimate = window?.sessionStorage?.getItem(DID_ANIMATE_STORAGE_KEY) === '1'
      return !didAnimate
    } catch (_) {
      return false
    }
  })

  const minimizeKey = useMemo(() => getMinimizeKey(action, user), [action, user])

  const onWaitlist = forcedOnWaitlist || derivedOnWaitlist

  const timersRef = useRef([])
  const clearTimers = () => {
    for (const t of timersRef.current) window.clearTimeout(t)
    timersRef.current = []
  }

  useEffect(() => () => clearTimers(), [])

  // Once backend state shows waitlist, we no longer need the forced flag.
  useEffect(() => {
    if (derivedOnWaitlist) setForcedOnWaitlist(false)
  }, [derivedOnWaitlist])

  useEffect(() => {
    // Sidebar open state is tracked via body class toggled in Header.jsx
    const update = () => {
      try {
        setSidebarOpen(document?.body?.classList?.contains('sidebar-open') || false)
      } catch (_) {
        setSidebarOpen(false)
      }
    }

    update()

    let observer = null
    try {
      observer = new MutationObserver(update)
      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    } catch (_) {
      observer = null
    }

    return () => observer?.disconnect?.()
  }, [])

  useEffect(() => {
    // Track mobile viewport changes
    const updateMobile = () => {
      try {
        setIsMobile(window?.innerWidth <= 768)
      } catch (_) {
        setIsMobile(false)
      }
    }

    updateMobile()

    let resizeTimer = null
    const handleResize = () => {
      if (resizeTimer) window.clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(updateMobile, 150)
    }

    try {
      window?.addEventListener('resize', handleResize)
    } catch (_) {
      // ignore
    }

    return () => {
      if (resizeTimer) window.clearTimeout(resizeTimer)
      try {
        window?.removeEventListener('resize', handleResize)
      } catch (_) {
        // ignore
      }
    }
  }, [])

  useEffect(() => {
    // If the action changes, only keep minimized state if it was minimized for THIS action.
    // For rolled-back users (step 5), default to minimized (mini-card).
    if (isRolledBack) {
      setMinimized(true)
      return
    }

    try {
      const saved = window?.localStorage?.getItem(MINIMIZED_STORAGE_KEY)
      setMinimized(!!minimizeKey && saved === minimizeKey)
    } catch (_) {
      setMinimized(false)
    }
  }, [minimizeKey, isRolledBack])

  useEffect(() => {
    // Fade in only once per tab/session (first page load).
    if (!action?.key) return
    if (minimized) return

    let didAnimate = false
    try {
      didAnimate = window?.sessionStorage?.getItem(DID_ANIMATE_STORAGE_KEY) === '1'
    } catch (_) {
      didAnimate = false
    }

    if (!didAnimate) {
      let animTimer = null
      const delayTimer = window.setTimeout(() => {
        setHiddenUntilAnimate(false)
        setAnimateIn(true)
        try {
          window?.sessionStorage?.setItem(DID_ANIMATE_STORAGE_KEY, '1')
        } catch (_) {
          // ignore
        }

        animTimer = window.setTimeout(() => setAnimateIn(false), 260)
      }, 3000)

      return () => {
        window.clearTimeout(delayTimer)
        if (animTimer) window.clearTimeout(animTimer)
      }
    }

    return undefined
  }, [action?.key, minimized])

  useEffect(() => {
    // If minimized, always show immediately (no hidden pre-animate state).
    if (minimized) setHiddenUntilAnimate(false)
  }, [minimized])

  // Allow rendering success card even when there is no current action.
  // IMPORTANT: keep this *after* hooks to avoid hook ordering issues.
  if (!action && !showSuccess && !(minimized && onWaitlist)) return null

  const setMinimizedPersist = next => {
    setMinimized(next)
    try {
      if (next) {
        if (minimizeKey) window?.localStorage?.setItem(MINIMIZED_STORAGE_KEY, minimizeKey)
      } else {
        window?.localStorage?.removeItem(MINIMIZED_STORAGE_KEY)
      }
    } catch (_) {
      // ignore
    }
  }

  const onClick = async () => {
    if (!action?.apiAction || loading) return
    const apiAction = action.apiAction

    // For new_dashboard_ready, just reload the page (cookie is already set)
    if (apiAction === 'new_dashboard_ready') {
      setLoading(true)
      deleteCookieByName('nf_ab')
      deleteHostCookieByName('nf_ab')
      setHostCookie('nf_ab', 'master', 30)
      document.cookie = 'newDashboardFirstUse=yes; path=/'
      window.location.reload()
      return
    }

    try {
      setLoading(true)
      // For join_waitlist, ensure minimum 2 second loading time
      const apiPromise = baseRequest.post('/oauth/manage-new-dashboard-access', {
        action: apiAction,
        source: DASHBOARD_FEEDBACK_SOURCE,
      })
      const minTimePromise =
        apiAction === 'join_waitlist'
          ? new Promise(resolve => window.setTimeout(resolve, 2000))
        : Promise.resolve()

      await Promise.all([apiPromise, minTimePromise])

      if (apiAction === 'join_waitlist') {
        // Join waitlist flow:
        // 1) Instantly hide current card
        // 2) Wait .1s
        // 3) Fade success card up/in for 10s
        // 4) Fade success card down/out
        setForcedOnWaitlist(true)
        clearTimers()

        setSuppressActionBanner(true)
        setLoading(false)
        setShowSuccess(false) // ensure clean slate

        // refresh user/session in background
        onUpdated?.()

        timersRef.current.push(
          window.setTimeout(() => {
            setShowSuccess(true)
            setIsSuccessExiting(false)
            // visible for 10s then fade out
            timersRef.current.push(
              window.setTimeout(() => {
                setIsSuccessExiting(true)
                timersRef.current.push(
                  window.setTimeout(() => {
                    setShowSuccess(false)
                    setIsSuccessExiting(false)
                    setSuppressActionBanner(false)
                  }, 230),
                )
              }, 10000),
            )
          }, 100),
        )
      } else {
        // Action completed, don't keep minimized state around
        setMinimizedPersist(false)
        await onUpdated?.()

        // After accepting invite, refresh the page to ensure the app picks up new dashboard access.
        if (apiAction === 'accept_invitation') {
          deleteCookieByName('nf_ab')
          deleteHostCookieByName('nf_ab')
          setHostCookie('nf_ab', 'master', 30)
          document.cookie = 'newDashboardFirstUse=yes; path=/'
          window.location.reload()
        }
      }
    } catch (err) {
      showAxiosError(err)
    } finally {
      setLoading(false)
    }
  }

  if (minimized) {
    const miniWrapperStyle = {
      ...styles.miniWrapper,
      left: sidebarOpen ? 210 : styles.miniWrapper.left,
      // Hide icon behind sidebar on mobile when sidebar is open
      ...(sidebarOpen && isMobile ? { display: 'none' } : {}),
    }

    return (
      <div
        className="ttf-dashboard-action-banner ttf-dashboard-action-banner--minimized"
        style={miniWrapperStyle}
        role="region"
        aria-label="Dashboard action minimized"
      >
        <style>{styles.mobile}</style>
        <style>{styles.buttonCss}</style>
        <button
          type="button"
          style={styles.miniIconBtn}
          aria-label={onWaitlist ? 'You are on the new dashboard waitlist' : 'Show dashboard action'}
          title={onWaitlist ? 'You are on the new dashboard waitlist' : 'Show dashboard action'}
          onClick={() => {
            // If there's an actionable banner, expand it; otherwise keep as passive reminder.
            if (action) setMinimizedPersist(false)
          }}
        >
          <svg
            className="ttf-dashboard-action-banner__mini-present"
            aria-hidden
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M3 9h18v4H3V9Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
            <path
              d="M5 13h14v8H5v-8Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
            <path
              d="M12 9v12"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <path
              d="M12 9c-1.5 0-4.5-.5-4.5-2.5S10.5 4 12 6.5C13.5 4 16.5 4.5 16.5 6.5S13.5 9 12 9Z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    )
  }

  if (showSuccess) {
    return (
      <div className="ttf-dashboard-action-banner" style={styles.wrapper} role="region" aria-label="Dashboard action">
        <style>{styles.mobile}</style>
        <style>{styles.buttonCss}</style>
        <div
          className={`ttf-dashboard-action-banner__card ${
            isSuccessExiting ? 'ttf-dashboard-action-banner__card--exit' : 'ttf-dashboard-action-banner__card--animate'
          }`}
          style={styles.card}
        >
          <button
            type="button"
            className="ttf-dashboard-action-banner__close"
            aria-label="Minimize"
            onClick={() => setMinimizedPersist(true)}
          >
            ×
          </button>
          <div className="ttf-dashboard-action-banner__text" style={styles.text}>
            <div className="ttf-dashboard-action-banner__eyebrow" style={styles.eyebrow}>
              You're on the waitlist!
            </div>
            <p className="ttf-dashboard-action-banner__title" style={styles.title}>
              We'll notify you once you have access
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (suppressActionBanner) {
    return null
  }

  return (
    <div className="ttf-dashboard-action-banner" style={styles.wrapper} role="region" aria-label="Dashboard action">
      <style>{styles.mobile}</style>
      <style>{styles.buttonCss}</style>
      <div
        className={`ttf-dashboard-action-banner__card ${
          hiddenUntilAnimate ? 'ttf-dashboard-action-banner__card--preanimate' : ''
        } ${animateIn ? 'ttf-dashboard-action-banner__card--animate' : ''}`}
        style={styles.card}
      >
        <button
          type="button"
          className="ttf-dashboard-action-banner__close"
          aria-label="Minimize"
          onClick={() => setMinimizedPersist(true)}
          disabled={loading}
        >
          ×
        </button>
        <div className="ttf-dashboard-action-banner__text" style={styles.text}>
          <div className="ttf-dashboard-action-banner__eyebrow" style={styles.eyebrow}>
            {action.eyebrow}
          </div>
          <p className="ttf-dashboard-action-banner__title" style={styles.title}>
            {action.title}
          </p>
        </div>
        <button
          type="button"
          className="ttf-dashboard-action-banner__btn"
          onClick={onClick}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="ttf-dashboard-action-banner__spinner" aria-hidden />
              <span className="ttf-dashboard-action-banner__sr-only">Working…</span>
            </>
          ) : (
            action.cta
          )}
        </button>
      </div>
    </div>
  )
}

DashboardActionBanner.propTypes = {
  user: PropTypes.object,
  onUpdated: PropTypes.func,
}

DashboardActionBanner.defaultProps = {
  user: null,
  onUpdated: null,
}

export default DashboardActionBanner

